import React, { useContext, useCallback, useMemo, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { Checkbox } from 'antd';
import { UserRoles, IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { ISelectOption } from 'components/Select/types';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setSelectedRestaurants: (value: string[]) => void;
	selectedRestaurants: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ data, isLoading, refetch, responseError, setSelectedRestaurants, selectedRestaurants }) => {
		const { loggedUser } = useContext<IAppContext>(AppContext);
		const [searchString, setSearchString] = useState<string>('');
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);
		const hasAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);

		const visibleData = useMemo(() => {
			return showArchived
				? data
				: data.filter((el) => el.status === EntityStatus.Active || selectedRestaurants.includes(el.id));
		}, [data, showArchived, selectedRestaurants]);

		const accessibleRestaurants = useMemo(() => {
			const restaurants = loggedUser.restaurants || [];
			return showArchived
				? restaurants
				: restaurants.filter((el) => el.status === EntityStatus.Active || selectedRestaurants.includes(el.id));
		}, [loggedUser, showArchived, selectedRestaurants]);

		const filteredRestaurants = useMemo(() => {
			return searchString
				? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: visibleData;
		}, [searchString, visibleData]);

		const mappedOptions = useMemo<ISelectOption[]>(() => {
			const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
			return filteredRestaurants.map((el) => ({
				id: el.id,
				name: el.name,
				disabled: !hasAccess && !accessibleRestaurantIds.includes(el.id),
				additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [filteredRestaurants, accessibleRestaurants, hasAccess]);

		const handleSelectAllRestaurants = useCallback(() => {
			if (hasAccess) {
				if (visibleData.length === selectedRestaurants.length) {
					setSelectedRestaurants([]);
				} else {
					const IDsOfAllRestaurants = visibleData.map((el) => el.id);
					setSelectedRestaurants(IDsOfAllRestaurants);
				}
			} else {
				const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
				if (accessibleRestaurantIds.every((el) => selectedRestaurants.includes(el))) {
					const filteredIDsSelectedRestaurant = selectedRestaurants.filter(
						(el) => !accessibleRestaurantIds.includes(el),
					);
					setSelectedRestaurants(filteredIDsSelectedRestaurant);
				} else {
					const filteredAccessibleRestaurantIds = accessibleRestaurantIds.filter(
						(el) => !selectedRestaurants.includes(el),
					);
					setSelectedRestaurants([...selectedRestaurants, ...filteredAccessibleRestaurantIds]);
				}
			}
		}, [setSelectedRestaurants, hasAccess, visibleData, selectedRestaurants, accessibleRestaurants]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const getIsCheckedByHasAccess = () => {
					return !!(visibleData.length && visibleData.length === selectedRestaurants.length);
				};

				const getIsCheckedByWithoutAccess = () => {
					const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
					return accessibleRestaurantIds.every((el) => selectedRestaurants.includes(el));
				};

				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={hasAccess ? getIsCheckedByHasAccess() : getIsCheckedByWithoutAccess()}
								disabled={hasAccess ? !visibleData.length : !accessibleRestaurants.length}
								onChange={handleSelectAllRestaurants}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllRestaurantsLabel"
									asFragment={true}
								/>
							</Checkbox>
							<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
								<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[
				visibleData,
				accessibleRestaurants,
				selectedRestaurants,
				handleSelectAllRestaurants,
				hasAccess,
				setSearchString,
				searchString,
				showArchived,
				toggleShowArchived,
			],
		);

		return (
			<OutsideFormSelect
				testId="MultipleSelect"
				name="IDsSelectedRestaurantsForModifierGroup"
				label="restaurantsLabel"
				value={selectedRestaurants}
				options={mappedOptions}
				fetchError={responseError}
				loading={isLoading}
				onRefetch={refetch}
				disabled={isLoading}
				onChange={setSelectedRestaurants}
				counterValue={selectedRestaurants.length}
				withValidation={false}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
